import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  ShoppingCart,
  Heart,
  Star,
  List,
  CheckSquare,
  Square,
  CalendarDays,
} from 'lucide-react';
import { useLanguage } from '../../contexts/useLanguage';
import { getCartItems, removeFromCart, toggleShare, updateCartItem, CartItem } from '../../services/cartService';
import { mockPackages } from '../../data/mockData';
import { User as UserType } from '../../types';
import type { ShoppingCartData } from './shoppingCartTypes';
import { computeCartSelectionTotals } from './checkoutFormTotals';
import { useWebDesignedCart } from '../../contexts/WebDesignedCartContext';
import MyCartSidebarLineItems from './MyCartSidebarLineItems';
import MyCartSidebarCheckoutFooter from './MyCartSidebarCheckoutFooter';
import MyCartSidebarSummaryPanel from './MyCartSidebarSummaryPanel';
import { GuestReservationStepTracker } from './GuestReservationStepTracker';
import type { MyCartWizardStep } from './myCartWizardTypes';
import { SheetBackButton } from './SheetBackButton';

interface MyCartSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  shoppingCartData?: ShoppingCartData | null;
  onUpdateShoppingCartData?: (data: ShoppingCartData) => void;
  currentUser?: UserType | null;
  onCheckout?: (selectedItems: CartItem[], shoppingCartData: any, paymentMode?: 'guest' | 'member') => void;
}

const MyCartSidebar: React.FC<MyCartSidebarProps> = ({ 
  isOpen, 
  onClose, 
  shoppingCartData,
  onUpdateShoppingCartData,
  currentUser,
  onCheckout 
}) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const cartCtx = useWebDesignedCart();
  const vumPromo = cartCtx?.vumPromo ?? null;
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [selectedSection, setSelectedSection] = useState<'all' | 'liked' | 'favorites'>('all');
  const [showCheckoutOptions, setShowCheckoutOptions] = useState(false);
  const [cartStep, setCartStep] = useState<MyCartWizardStep>(1);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [editQuantity, setEditQuantity] = useState<number>(1);

  // Load cart items and listen for updates
  useEffect(() => {
    const loadCartItems = () => {
      const items = getCartItems();
      setCartItems(items);
      
      // If the item being edited was removed, cancel edit mode
      if (editingItemId && !items.find(item => item.id === editingItemId)) {
        setEditingItemId(null);
      }
    };
    
    loadCartItems();
    
    const handleCartUpdate = () => {
      loadCartItems();
    };
    
    window.addEventListener('cartUpdated', handleCartUpdate);
    return () => {
      window.removeEventListener('cartUpdated', handleCartUpdate);
    };
  }, [editingItemId]);

  const handleRemoveFromCart = (itemId: string) => {
    removeFromCart(itemId);
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      newSet.delete(itemId);
      return newSet;
    });
  };
  
  const handleToggleShare = (itemId: string) => {
    toggleShare(itemId);
  };

  const handleToggleSelect = (itemId: string) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedItems.size === cartItems.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(cartItems.map(item => item.id)));
    }
  };

  const calculatedTotals = useMemo(() => {
    const selected = cartItems.filter((item) => selectedItems.has(item.id));
    return computeCartSelectionTotals(selected, shoppingCartData ?? undefined, vumPromo);
  }, [cartItems, selectedItems, shoppingCartData, vumPromo]);

  useEffect(() => {
    if (selectedItems.size === 0) {
      setCartStep(1);
      setShowCheckoutOptions(false);
    }
  }, [selectedItems.size]);

  const hasTravelInfoReady = () =>
    !!(shoppingCartData?.startDate && shoppingCartData?.endDate);

  const goToCartStep = (step: MyCartWizardStep) => {
    if (step >= 2 && selectedItems.size === 0) return;
    if (step === 3 && !hasTravelInfoReady()) return;
    if (step === 3 && calculatedTotals.vumAnyCapacityExceeded) return;
    setCartStep(step);
    if (step < 3) setShowCheckoutOptions(false);
  };

  const handleCartBack = () => {
    if (cartStep > 1) {
      setCartStep((cartStep - 1) as MyCartWizardStep);
      setShowCheckoutOptions(false);
      return;
    }
    onClose();
  };

  const openTravelFromCart = () => {
    onClose();
    cartCtx?.openBookingAssistant({ returnToCart: true });
  };

  const getItemDetailRoute = (item: CartItem): string | null => {
    if (item.itemId.startsWith('vum-included-')) return null;
    if (item.type === 'accommodation') return `/webdesigned/stays/${item.itemId}`;
    if (item.type === 'activity') return `/webdesigned/activities/${item.itemId}`;
    return `/webdesigned/forfaits/${item.itemId}`;
  };

  const handleOpenItemDetails = (item: CartItem) => {
    const route = getItemDetailRoute(item);
    if (!route) return;
    navigate({ to: route });
    onClose();
  };

  const recommendedBundles = useMemo(() => {
    if (cartItems.length === 0) return [];

    const toBundleKey = (item: CartItem) =>
      `${item.type === 'accommodation' ? 'stay' : item.type}:${item.itemId}`;

    const inCartKeys = new Set(cartItems.map(toBundleKey));
    const preferenceKeys = new Set(
      cartItems.filter((item) => item.isLiked || item.isFavorite).map(toBundleKey),
    );

    const scored = mockPackages
      .map((pkg) => {
        const productKeys = pkg.products.map((p) => `${p.type}:${p.id}`);
        const matchedInCart = productKeys.filter((key) => inCartKeys.has(key));
        const matchedPreferences = productKeys.filter((key) => preferenceKeys.has(key));
        const score = matchedInCart.length + matchedPreferences.length * 2;
        return { pkg, matchedInCart, matchedPreferences, score };
      })
      .filter((row) => row.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 3);

    return scored;
  }, [cartItems]);

  const handleCheckout = (paymentMode?: 'guest' | 'member') => {
    if (selectedItems.size === 0) {
      return; // No items selected
    }

    const selected = cartItems.filter(item => selectedItems.has(item.id));
    
    // If payment mode is specified, proceed directly
    if (paymentMode && onCheckout) {
      setShowCheckoutOptions(false);
      console.log('Checkout with payment mode:', paymentMode, 'Selected items:', selected);
      onCheckout(selected, shoppingCartData, paymentMode);
      return;
    }

    // If logged in, proceed as member
    if (currentUser && onCheckout) {
      console.log('Checkout as member, Selected items:', selected);
      onCheckout(selected, shoppingCartData, 'member');
      return;
    }

    // If not logged in, show options
    setShowCheckoutOptions(true);
  };

  if (!isOpen) return null;

  const hasTravelInfo = hasTravelInfoReady();
  const step1Done = selectedItems.size > 0;
  const step2Done = step1Done && hasTravelInfo && !calculatedTotals.vumAnyCapacityExceeded;

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden bg-[#ece3de]">
        {/* Header — back steps through wizard, then closes */}
        <div className="flex flex-shrink-0 items-center justify-between border-b border-black/10 p-3">
          <div className="flex min-w-0 items-center gap-2">
            {/* Phone: floating bottom-left thumb zone; pad/desktop: header inline */}
            <SheetBackButton onClick={handleCartBack} />
            <div className="flex-shrink-0 rounded-lg bg-black/10 p-2">
              <ShoppingCart className="h-5 w-5 text-[#3d2817]" />
            </div>
            <h2 className="truncate text-base font-bold text-[#3d2817] md:text-lg">
              {t('shoppingCart.myCart')}
            </h2>
            {cartItems.length > 0 && (
              <span className="rounded-full bg-[#2f9e44] px-2 py-0.5 text-xs font-semibold text-white">
                {cartItems.length > 999 ? '999+' : cartItems.length}
              </span>
            )}
          </div>
        </div>

        {selectedItems.size > 0 ? (
          <div className="flex-shrink-0 border-b border-black/10 px-3 py-2">
            <GuestReservationStepTracker
              step={cartStep}
              step1Done={step1Done}
              step2Done={step2Done}
              step3Done={false}
              labels={[
                t('shoppingCart.stepItems') || 'Items',
                t('shoppingCart.stepSummary') || 'Summary',
                t('shoppingCart.stepCheckout') || 'Checkout',
              ]}
              onSelectStep={(s) => goToCartStep(s)}
            />
          </div>
        ) : null}

        {!hasTravelInfo && cartStep === 1 ? (
          <div className="flex-shrink-0 border-b border-amber-200/80 bg-amber-50/90 px-3 py-3">
            <div className="flex items-start gap-2.5 text-amber-900">
              <CalendarDays className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold">
                  {t('shoppingCart.travelInfoRequired') || 'Travel info required'}
                </p>
                <p className="mt-0.5 text-xs text-amber-800">
                  {t('shoppingCart.travelInfoRequiredDesc')}
                </p>
                <button
                  type="button"
                  className="mt-2 text-xs font-semibold text-[#2f9e44] underline-offset-2 hover:underline"
                  onClick={openTravelFromCart}
                >
                  {t('shoppingCart.openTravelAssistant') || 'Open calendar assistant'}
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {/* Step 1 window — select items (calculation stays out of the way) */}
        {cartStep === 1 ? (
          <>
            <div className="flex border-b border-black/10 bg-[#e8ddd7]/60 px-3 pt-2">
              <button
                onClick={() => setSelectedSection('all')}
                className={`flex items-center space-x-2 border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
                  selectedSection === 'all'
                    ? 'border-[#2f9e44] text-[#2f9e44]'
                    : 'border-transparent text-[#5d4437] hover:text-[#3d2817]'
                }`}
              >
                <List className={`h-4 w-4 ${selectedSection === 'all' ? 'fill-current' : ''}`} />
                <span className="rounded-full bg-white/80 px-2 py-0.5 text-xs text-[#3d2817]">
                  {cartItems.length}
                </span>
              </button>
              <button
                onClick={() => setSelectedSection('liked')}
                className={`flex items-center space-x-2 border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
                  selectedSection === 'liked'
                    ? 'border-[#2f9e44] text-[#2f9e44]'
                    : 'border-transparent text-[#5d4437] hover:text-[#3d2817]'
                }`}
              >
                <Heart className={`h-4 w-4 ${selectedSection === 'liked' ? 'fill-current' : ''}`} />
                <span className="rounded-full bg-white/80 px-2 py-0.5 text-xs text-[#3d2817]">
                  {cartItems.filter((item) => item.isLiked).length}
                </span>
              </button>
              <button
                onClick={() => setSelectedSection('favorites')}
                className={`flex items-center space-x-2 border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
                  selectedSection === 'favorites'
                    ? 'border-[#2f9e44] text-[#2f9e44]'
                    : 'border-transparent text-[#5d4437] hover:text-[#3d2817]'
                }`}
              >
                <Star className={`h-4 w-4 ${selectedSection === 'favorites' ? 'fill-current' : ''}`} />
                <span className="rounded-full bg-white/80 px-2 py-0.5 text-xs text-[#3d2817]">
                  {cartItems.filter((item) => item.isFavorite).length}
                </span>
              </button>
            </div>

            {cartItems.length > 0 && (
              <div className="flex items-center justify-between border-b border-black/10 bg-[#ece3de] px-3 py-2.5">
                <button
                  type="button"
                  onClick={handleSelectAll}
                  aria-label={
                    selectedItems.size === cartItems.length
                      ? t('common.deselectAll')
                      : t('common.selectAll')
                  }
                  className="flex items-center space-x-2 text-sm font-medium text-[#3d2817] transition-colors hover:text-[#2f9e44]"
                >
                  {selectedItems.size === cartItems.length ? (
                    <CheckSquare className="h-5 w-5 text-[#2f9e44]" aria-hidden />
                  ) : (
                    <Square className="h-5 w-5 text-[#3d2817]/40" aria-hidden />
                  )}
                  <span>{t('common.selectAll')}</span>
                </button>
                <span className="text-sm text-[#5d4437]">
                  {selectedItems.size}{' '}
                  {selectedItems.size === 1 ? t('common.item') : t('common.items')}{' '}
                  {t('common.selected')}
                </span>
              </div>
            )}

            <div className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain touch-pan-y [-webkit-overflow-scrolling:touch] px-3 py-3 pb-3">
              <MyCartSidebarLineItems
                t={t}
                cartItems={cartItems}
                selectedSection={selectedSection}
                selectedItems={selectedItems}
                shoppingCartData={shoppingCartData ?? null}
                vumPromo={vumPromo}
                calculatedTotals={calculatedTotals}
                editingItemId={editingItemId}
                setEditingItemId={setEditingItemId}
                editQuantity={editQuantity}
                setEditQuantity={setEditQuantity}
                handleToggleSelect={handleToggleSelect}
                handleOpenItemDetails={handleOpenItemDetails}
                handleRemoveFromCart={handleRemoveFromCart}
                handleToggleShare={handleToggleShare}
              />

              {recommendedBundles.length > 0 && (
                <div className="mt-6 border-t border-[#3d2817]/10 pt-4">
                  <h4 className="mb-3 text-sm font-semibold text-[#3d2817]">
                    {t('common.youMightLike') || 'You might like'}
                  </h4>
                  <div className="space-y-2">
                    {recommendedBundles.map(({ pkg, matchedInCart, matchedPreferences }) => (
                      <div
                        key={`rec-${pkg.id}`}
                        className="rounded-xl border border-[#3d2817]/12 bg-white/90 p-3 shadow-sm transition-shadow hover:shadow-md"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="truncate text-sm font-semibold text-[#3d2817]">{pkg.name}</p>
                            <p className="line-clamp-2 text-xs text-[#5d4437]">{pkg.description}</p>
                            <p className="mt-1 text-xs text-[#2f9e44]">
                              {matchedInCart.length} in cart
                              {matchedPreferences.length > 0
                                ? ` • ${matchedPreferences.length} liked/favorite`
                                : ''}
                            </p>
                          </div>
                          <div className="shrink-0 text-right">
                            <p className="text-sm font-bold text-[#2f9e44]">${pkg.price}</p>
                            <button
                              type="button"
                              onClick={() => {
                                navigate({ to: `/webdesigned/forfaits/${pkg.id}` });
                                onClose();
                              }}
                              className="mt-1 rounded-lg bg-[#2f9e44] px-2 py-1 text-xs text-white transition-colors hover:bg-[#27863a]"
                            >
                              {t('common.viewDetails') || 'View details'}
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain touch-pan-y [-webkit-overflow-scrolling:touch] px-3 py-3">
            {cartStep === 2 ? (
              <MyCartSidebarSummaryPanel
                t={t}
                shoppingCartData={shoppingCartData}
                calculatedTotals={calculatedTotals}
                vumPromo={vumPromo}
                noDatesBlocked={!hasTravelInfo}
                onOpenTravelAssistant={openTravelFromCart}
              />
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-[#5d4437]">
                  {t('shoppingCart.stepCheckoutHint') ||
                    'Choose how you want to complete your order.'}
                </p>
                <div className="rounded-xl border border-[#3d2817]/10 bg-white/70 px-3 py-2 text-sm text-[#3d2817]">
                  <span className="font-semibold">{selectedItems.size}</span>{' '}
                  {selectedItems.size === 1 ? t('common.item') : t('common.items')}{' '}
                  {t('common.selected')}
                  <span className="mt-1 block text-base font-bold text-[#2f9e44]">
                    {t('common.total')}: ${calculatedTotals.total.toFixed(2)}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        {selectedItems.size > 0 && (
          <div className="flex-shrink-0">
            <MyCartSidebarCheckoutFooter
              layout="pinned"
              t={t}
              shoppingCartData={shoppingCartData}
              calculatedTotals={calculatedTotals}
              showCheckoutOptions={showCheckoutOptions}
              setShowCheckoutOptions={setShowCheckoutOptions}
              currentUser={currentUser}
              selectedItems={selectedItems}
              handleCheckout={handleCheckout}
              vumPromo={vumPromo}
              cartStep={cartStep}
              onGoToStep={goToCartStep}
            />
          </div>
        )}
    </div>
  );
};

export default MyCartSidebar;
